import { Controller } from "../../lib/stimulus-shim"

export default class extends Controller {
  static values = { 
    profileId: String,
    pageId: String,
    moduleIds: Array
  }

  connect() {
    // Propriétaire : aucune collecte
    if (document.querySelector('meta[name="rho-owner"]')) return

    // Only track additional viewport data if not already tracked server-side
    this.enrichPageViewData()
    this.setupScrollTracking()
    this.setupTimeTracking()
    this.setupModuleTracking()
  }

  disconnect() {
    this.saveTimeSpent()
    if (this.scrollObserver) {
      this.scrollObserver.disconnect()
    }
  }

  enrichPageViewData() {
    if (typeof ahoy === "undefined") return
    if (!this.pageIdValue) return // Skip if no page ID

    // Send viewport dimensions as a separate event to enrich the page visit
    ahoy.track("page_view_enrichment", {
      profile_id: this.profileIdValue,
      page_id: this.pageIdValue,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      device_pixel_ratio: window.devicePixelRatio || 1
    })
  }

  setupScrollTracking() {
    this.maxScrollDepth = 0
    this.checkpoints = [25, 50, 75, 100]
    this.reachedCheckpoints = new Set()

    const throttle = (func, delay) => {
      let timeoutId
      let lastExecTime = 0
      return function (...args) {
        const currentTime = Date.now()
        if (currentTime - lastExecTime > delay) {
          func.apply(this, args)
          lastExecTime = currentTime
        } else {
          clearTimeout(timeoutId)
          timeoutId = setTimeout(() => {
            func.apply(this, args)
            lastExecTime = Date.now()
          }, delay - (currentTime - lastExecTime))
        }
      }
    }

    const handleScroll = throttle(() => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight
      const scrollPercent = scrollHeight > 0 ? (window.scrollY / scrollHeight) * 100 : 0
      
      this.maxScrollDepth = Math.max(this.maxScrollDepth, scrollPercent)
      
      this.checkpoints.forEach(checkpoint => {
        if (scrollPercent >= checkpoint && !this.reachedCheckpoints.has(checkpoint)) {
          this.reachedCheckpoints.add(checkpoint)
          this.trackScrollDepth(checkpoint)
        }
      })
    }, 200)

    window.addEventListener('scroll', handleScroll)
    
    // Track initial scroll position
    handleScroll()
  }

  trackScrollDepth(depth) {
    if (typeof ahoy === "undefined") return

    ahoy.track("scroll_depth", {
      profile_id: this.profileIdValue,
      page_id: this.pageIdValue,
      depth_percentage: depth
    })
  }

  setupTimeTracking() {
    this.startTime = Date.now()
    this.isVisible = true
    this.totalTimeSpent = 0

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.totalTimeSpent += Date.now() - this.startTime
        this.isVisible = false
      } else {
        this.startTime = Date.now()
        this.isVisible = true
      }
    })

    // Save time spent every 30 seconds
    this.timeInterval = setInterval(() => {
      this.saveTimeSpent()
    }, 30000)
  }

  saveTimeSpent() {
    if (typeof ahoy === "undefined") return

    const currentSession = this.isVisible ? Date.now() - this.startTime : 0
    const timeSpent = Math.round((this.totalTimeSpent + currentSession) / 1000) // in seconds

    if (timeSpent > 0) {
      ahoy.track("time_on_page", {
        profile_id: this.profileIdValue,
        page_id: this.pageIdValue,
        time_spent_seconds: timeSpent,
        max_scroll_depth: Math.round(this.maxScrollDepth)
      })
    }

    if (this.timeInterval) {
      clearInterval(this.timeInterval)
    }
  }

  setupModuleTracking() {
    if (!this.moduleIdsValue || this.moduleIdsValue.length === 0) return

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: [0.25, 0.5, 0.75, 1.0]
    }

    this.moduleVisibility = new Map()
    this.moduleStartTimes = new Map()

    this.scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const moduleId = entry.target.dataset.moduleId
        
        if (entry.isIntersecting) {
          if (!this.moduleStartTimes.has(moduleId)) {
            this.moduleStartTimes.set(moduleId, Date.now())
          }
          
          const currentVisibility = this.moduleVisibility.get(moduleId) || 0
          if (entry.intersectionRatio > currentVisibility) {
            this.moduleVisibility.set(moduleId, entry.intersectionRatio)
            
            if (entry.intersectionRatio >= 0.5) {
              this.trackModuleView(moduleId)
            }
          }
        } else {
          // Module is out of view
          if (this.moduleStartTimes.has(moduleId)) {
            const timeSpent = Date.now() - this.moduleStartTimes.get(moduleId)
            this.trackModuleEngagement(moduleId, timeSpent)
            this.moduleStartTimes.delete(moduleId)
          }
        }
      })
    }, observerOptions)

    // Observe all modules
    this.moduleIdsValue.forEach(moduleId => {
      const element = document.querySelector(`[data-module-id="${moduleId}"]`)
      if (element) {
        this.scrollObserver.observe(element)
      }
    })
  }

  trackModuleView(moduleId) {
    if (typeof ahoy === "undefined") return

    ahoy.track("module_view", {
      profile_id: this.profileIdValue,
      page_id: this.pageIdValue,
      module_id: moduleId
    })
  }

  trackModuleEngagement(moduleId, timeSpent) {
    if (typeof ahoy === "undefined" || timeSpent < 1000) return // Only track if viewed for more than 1 second

    ahoy.track("module_engagement", {
      profile_id: this.profileIdValue,
      page_id: this.pageIdValue,
      module_id: moduleId,
      time_spent_ms: timeSpent
    })
  }

  // Public method to track clicks on modules
  trackModuleClick(event) {
    const moduleEl = event.currentTarget.closest('[data-module-id]')
    if (!moduleEl) return

    const moduleId = moduleEl.dataset.moduleId
    const clickTarget = event.target.tagName.toLowerCase()
    const clickText = event.target.textContent?.substring(0, 50)

    if (typeof ahoy !== "undefined") {
      ahoy.track("module_click", {
        profile_id: this.profileIdValue,
        page_id: this.pageIdValue,
        module_id: moduleId,
        click_target: clickTarget,
        click_text: clickText,
        click_x: event.clientX,
        click_y: event.clientY
      })
    }
  }
}